// src/services/stockMovementService.js
// Stock Movement Logging Service
// Provides comprehensive audit trail for all stock changes across the system

import { db } from './firebase';
import {
    collection,
    addDoc,
    query,
    where,
    orderBy,
    getDocs,
    Timestamp,
    limit as firestoreLimit
} from 'firebase/firestore';

// ============================================================================
// CONSTANTS
// ============================================================================

export const MOVEMENT_COLLECTION = 'stockMovements';

export const MOVEMENT_TYPE = {
    ORDER: 'order',                    // Customer order deduction - LEGACY (now RESERVATION)
    VENUE_ORDER: 'venue_order',        // Venue order deduction - LEGACY (now RESERVATION)
    PACKING: 'packing',                // Packing operation deduction
    PRODUCTION: 'production',          // Production packaging addition
    STOCK_TAKE: 'stock_take',          // Stock count reconciliation
    ADJUSTMENT: 'adjustment',          // Manual adjustment
    VENUE_TRANSFER: 'venue_transfer',  // Transfer between locations
    RECONCILIATION: 'reconciliation',  // POS reconciliation (Zettle)
    FREEZE: 'freeze',                  // Move usable stock into frozen lots
    DEFROST: 'defrost',                // Move frozen lots back to usable
    // PHASE B: Reservation workflow types
    RESERVATION: 'reservation',                  // Stock reserved for order (not deducted)
    RESERVATION_RELEASE: 'reservation_release',  // Reservation cancelled/expired
    DISPATCH: 'dispatch',                        // Confirmed dispatch (actual deduction)
    VARIANCE_ADJUSTMENT: 'variance_adjustment',  // Fixing stock discrepancies
    RELEASE: 'release'                           // LEGACY: Use RESERVATION_RELEASE instead
};

export const SOURCE_SYSTEM = {
    ECOMMERCE: 'ecommerce',
    PRODUCTION: 'production',
    PACKERS: 'packers',
    VENUE: 'venue',
    ADMIN: 'admin',
    STOCK_TAKE: 'stock_take',
    SYSTEM: 'system',
    // PHASE B: New workflow systems
    DISPATCH_CONFIRMATION: 'dispatch_confirmation',  // Dispatch confirmation UI
    RECONCILIATION: 'reconciliation',                // Week-end reconciliation UI (different from POS recon)
    PACKER: 'packer'                                 // Packer's toolkit dispatch
};

// ============================================================================
// STOCK MOVEMENT SERVICE
// ============================================================================

class StockMovementService {
    /**
     * Log a stock movement
     * @param {object} movement - Movement details
     * @returns {Promise<string>} Movement document ID
     */
    async logMovement(movement) {
        const {
            sku,
            lotCode = null,
            type,
            deltaUnits,
            beforeTotal,
            afterTotal,
            sourceSystem,
            sourceId = null,
            userId,
            reason = '',
            metadata = {}
        } = movement;

        // Validation
        if (!sku) throw new Error('SKU is required for stock movement');
        if (!type) throw new Error('Movement type is required');
        if (typeof deltaUnits !== 'number') throw new Error('deltaUnits must be a number');
        if (!sourceSystem) throw new Error('Source system is required');

        try {
            const movementDoc = {
                sku,
                lotCode,
                type,
                deltaUnits,
                beforeTotal: beforeTotal || 0,
                afterTotal: afterTotal || 0,
                netChange: (afterTotal || 0) - (beforeTotal || 0),
                
                sourceSystem,
                sourceId,
                
                userId: userId || 'system',
                timestamp: Timestamp.now(),
                reason,
                metadata,
                
                // For reporting/analytics
                isAddition: deltaUnits > 0,
                isDeduction: deltaUnits < 0,
                absValue: Math.abs(deltaUnits)
            };

            const docRef = await addDoc(collection(db, MOVEMENT_COLLECTION), movementDoc);

            console.log(`[StockMovement] Logged ${type} for ${sku}: ${deltaUnits} units`, {
                movementId: docRef.id,
                before: beforeTotal,
                after: afterTotal,
                sourceSystem
            });

            return docRef.id;

        } catch (error) {
            console.error('[StockMovement] Error logging movement:', error);
            // Don't throw - logging shouldn't break stock operations
            // but log the error for monitoring
            console.error('[StockMovement] Failed to log movement:', {
                sku,
                type,
                deltaUnits,
                error: error.message
            });
            return null;
        }
    }

    /**
     * Log multiple movements in batch
     * @param {Array} movements - Array of movement objects
     * @returns {Promise<Array>} Array of movement IDs
     */
    async logBatchMovements(movements) {
        if (!Array.isArray(movements) || movements.length === 0) {
            throw new Error('Movements array is required');
        }

        try {
            const movementIds = await Promise.all(
                movements.map(movement => this.logMovement(movement))
            );

            console.log(`[StockMovement] Logged ${movementIds.length} batch movements`);

            return movementIds.filter(id => id !== null);

        } catch (error) {
            console.error('[StockMovement] Error in batch logging:', error);
            return [];
        }
    }

    /**
     * Get stock movement history for a SKU
     * @param {string} sku - Product SKU
     * @param {object} options - Query options
     * @returns {Promise<Array>} Array of movements
     */
    async getStockHistory(sku, options = {}) {
        if (!sku) throw new Error('SKU is required');

        const {
            startDate = null,
            endDate = null,
            type = null,
            limit = 100,
            sortOrder = 'desc' // desc = most recent first
        } = options;

        try {
            let q = query(
                collection(db, MOVEMENT_COLLECTION),
                where('sku', '==', sku),
                orderBy('timestamp', sortOrder)
            );

            // Filter by date range
            if (startDate) {
                const startTimestamp = startDate instanceof Timestamp ? 
                    startDate : Timestamp.fromDate(new Date(startDate));
                q = query(q, where('timestamp', '>=', startTimestamp));
            }

            if (endDate) {
                const endTimestamp = endDate instanceof Timestamp ? 
                    endDate : Timestamp.fromDate(new Date(endDate));
                q = query(q, where('timestamp', '<=', endTimestamp));
            }

            // Filter by type
            if (type) {
                q = query(q, where('type', '==', type));
            }

            // Apply limit
            if (limit) {
                q = query(q, firestoreLimit(limit));
            }

            const snap = await getDocs(q);
            const movements = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate?.() || new Date()
            }));

            console.log(`[StockMovement] Fetched ${movements.length} movements for ${sku}`);

            return movements;

        } catch (error) {
            console.error('[StockMovement] Error fetching stock history:', error);
            throw error;
        }
    }

    /**
     * Get all movements for a date range (across all SKUs)
     * @param {object} options - Query options
     * @returns {Promise<Array>} Array of movements
     */
    async getAllMovements(options = {}) {
        const {
            startDate = null,
            endDate = null,
            sourceSystem = null,
            type = null,
            limit = 500
        } = options;

        try {
            let q = query(
                collection(db, MOVEMENT_COLLECTION),
                orderBy('timestamp', 'desc')
            );

            // Filter by date range
            if (startDate) {
                const startTimestamp = startDate instanceof Timestamp ? 
                    startDate : Timestamp.fromDate(new Date(startDate));
                q = query(q, where('timestamp', '>=', startTimestamp));
            }

            if (endDate) {
                const endTimestamp = endDate instanceof Timestamp ? 
                    endDate : Timestamp.fromDate(new Date(endDate));
                q = query(q, where('timestamp', '<=', endTimestamp));
            }

            // Filter by source system
            if (sourceSystem) {
                q = query(q, where('sourceSystem', '==', sourceSystem));
            }

            // Filter by type
            if (type) {
                q = query(q, where('type', '==', type));
            }

            // Apply limit
            if (limit) {
                q = query(q, firestoreLimit(limit));
            }

            const snap = await getDocs(q);
            const movements = snap.docs.map(doc => ({
                id: doc.id,
                ...doc.data(),
                timestamp: doc.data().timestamp?.toDate?.() || new Date()
            }));

            console.log(`[StockMovement] Fetched ${movements.length} total movements`);

            return movements;

        } catch (error) {
            console.error('[StockMovement] Error fetching all movements:', error);
            throw error;
        }
    }

    /**
     * Get movement summary/stats for a SKU
     * @param {string} sku - Product SKU
     * @param {object} options - Query options
     * @returns {Promise<object>} Movement statistics
     */
    async getMovementStats(sku, options = {}) {
        const { startDate = null, endDate = null } = options;

        try {
            const movements = await this.getStockHistory(sku, {
                startDate,
                endDate,
                limit: 1000 // Get more for stats
            });

            const stats = {
                totalMovements: movements.length,
                additions: movements.filter(m => m.isAddition).length,
                deductions: movements.filter(m => m.isDeduction).length,
                totalAdded: movements
                    .filter(m => m.isAddition)
                    .reduce((sum, m) => sum + m.absValue, 0),
                totalDeducted: movements
                    .filter(m => m.isDeduction)
                    .reduce((sum, m) => sum + m.absValue, 0),
                netChange: movements.reduce((sum, m) => sum + m.deltaUnits, 0),
                
                // By type
                byType: {},
                bySystem: {},
                
                // First and last movement
                firstMovement: movements[movements.length - 1] || null,
                lastMovement: movements[0] || null
            };

            // Group by type
            movements.forEach(m => {
                stats.byType[m.type] = (stats.byType[m.type] || 0) + 1;
                stats.bySystem[m.sourceSystem] = (stats.bySystem[m.sourceSystem] || 0) + 1;
            });

            console.log(`[StockMovement] Stats for ${sku}:`, {
                total: stats.totalMovements,
                netChange: stats.netChange,
                added: stats.totalAdded,
                deducted: stats.totalDeducted
            });

            return stats;

        } catch (error) {
            console.error('[StockMovement] Error calculating stats:', error);
            throw error;
        }
    }

    /**
     * Get recent movements (last 24 hours by default)
     * @param {number} hours - Hours to look back
     * @returns {Promise<Array>} Recent movements
     */
    async getRecentMovements(hours = 24) {
        const now = new Date();
        const startDate = new Date(now.getTime() - hours * 60 * 60 * 1000);

        return await this.getAllMovements({
            startDate,
            limit: 200
        });
    }

    /**
     * Check if stock movement logging is working
     * @returns {Promise<boolean>} Health check result
     */
    async healthCheck() {
        try {
            // Try to fetch recent movements
            const recentCount = query(
                collection(db, MOVEMENT_COLLECTION),
                orderBy('timestamp', 'desc'),
                firestoreLimit(1)
            );

            await getDocs(recentCount);

            console.log('[StockMovement] Health check passed');
            return true;

        } catch (error) {
            console.error('[StockMovement] Health check failed:', error);
            return false;
        }
    }
}

// ============================================================================
// SINGLETON INSTANCE
// ============================================================================

const stockMovementService = new StockMovementService();

export default stockMovementService;

// Export the class for testing
export { StockMovementService };

